import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DeviceScreenLayout from "../../components/layouts/DeviceScreenLayout";
import { useAuth } from "../../contexts/AuthContext";
import { getMyStores, getStoreChannels, getStoreDevice, getStoreVirtualNumber } from "../../api/stores";

const SelectionCard = ({ title, subtitle, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                padding: '24px 28px',
                background: '#fff',
                border: `1.5px solid ${hovered ? '#477FEE' : '#E5E8EE'}`,
                borderRadius: '16px',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                boxShadow: hovered ? '0 0 0 3px rgba(71,127,238,0.12)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
        >
            <span style={{
                fontFamily: 'Pretendard',
                fontSize: '17px',
                fontWeight: 600,
                color: '#1A1E27',
                letterSpacing: '-0.4px',
            }}>
                {title}
            </span>
            {subtitle && (
                <span style={{
                    fontFamily: 'Pretendard',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#8A94A6',
                    letterSpacing: '-0.3px',
                }}>
                    {subtitle}
                </span>
            )}
        </button>
    );
};

const BackButton = ({ onClick }) => (
    <button
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 0',
            fontFamily: 'Pretendard',
            fontSize: '14px',
            fontWeight: 500,
            color: '#8A94A6',
            letterSpacing: '-0.3px',
        }}
    >
        ← 이전
    </button>
);

const StepHeader = ({ step, title, description }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{
            fontFamily: 'Pretendard',
            fontSize: '12px',
            fontWeight: 600,
            color: '#477FEE',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
        }}>
            STEP {step} / 2
        </span>
        <span style={{
            fontFamily: 'Pretendard',
            fontSize: '24px',
            fontWeight: 700,
            color: '#1A1E27',
            letterSpacing: '-0.5px',
        }}>
            {title}
        </span>
        <span style={{
            fontFamily: 'Pretendard',
            fontSize: '14px',
            fontWeight: 400,
            color: '#8A94A6',
            letterSpacing: '-0.3px',
        }}>
            {description}
        </span>
    </div>
);

const CATEGORY_LABEL = {
    HAIR_NAIL: '헤어 · 네일',
    RESTAURANT: '음식점',
    CAFE: '카페',
    FITNESS: '피트니스',
    BEAUTY: '뷰티',
};

const OnboardingPage = () => {
    const navigate = useNavigate();
    const { selectStore, selectChannel, selectDevice, saveVirtualNumber } = useAuth();

    const [step, setStep] = useState(1);
    const [stores, setStores] = useState([]);
    const [channels, setChannels] = useState([]);
    const [pickedStore, setPickedStore] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Step 1: 스토어 목록 로드
    useEffect(() => {
        setIsLoading(true);
        setError(null);
        getMyStores()
            .then(setStores)
            .catch(() => setError('스토어 목록을 불러오지 못했어요.'))
            .finally(() => setIsLoading(false));
    }, []);

    // Step 2: 선택된 스토어의 채널 로드
    useEffect(() => {
        if (!pickedStore) return;
        setIsLoading(true);
        setError(null);
        getStoreChannels(pickedStore.uuid)
            .then(setChannels)
            .catch(() => setError('채널 목록을 불러오지 못했어요.'))
            .finally(() => setIsLoading(false));
    }, [pickedStore]);

    const handleStoreSelect = (store) => {
        setPickedStore(store);
        setStep(2);
    };

    const handleChannelSelect = async (channel) => {
        selectStore(pickedStore);
        selectChannel(channel);
        await Promise.allSettled([
            getStoreDevice(pickedStore.uuid).then((device) => { if (device) selectDevice(device); }),
            getStoreVirtualNumber(pickedStore.uuid).then((number) => { if (number) saveVirtualNumber(number); }),
        ]);
        navigate('/', { replace: true });
    };

    const handleBack = () => {
        setStep(1);
        setPickedStore(null);
        setChannels([]);
        setError(null);
    };

    const isEmpty = !isLoading && !error && (step === 1 ? stores : channels).length === 0;

    return (
        <DeviceScreenLayout>
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '48px',
                background: '#F3F4F6',
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: '460px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '28px',
                }}>
                    {step === 2 && <BackButton onClick={handleBack} />}

                    <StepHeader
                        step={step}
                        title={step === 1 ? '스토어 선택' : '채널 선택'}
                        description={
                            step === 1
                                ? '사용할 스토어를 선택해주세요'
                                : `${pickedStore?.name}의 채널을 선택해주세요`
                        }
                    />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {isLoading && (
                            <span style={{ fontFamily: 'Pretendard', color: '#8A94A6', fontSize: '14px' }}>
                                불러오는 중...
                            </span>
                        )}
                        {error && (
                            <span style={{ fontFamily: 'Pretendard', color: '#FF5F67', fontSize: '14px' }}>
                                {error}
                            </span>
                        )}
                        {isEmpty && (
                            <span style={{ fontFamily: 'Pretendard', color: '#8A94A6', fontSize: '14px' }}>
                                {step === 1 ? '등록된 스토어가 없어요.' : '등록된 채널이 없어요.'}
                            </span>
                        )}

                        {step === 1 && stores.map((store) => (
                            <SelectionCard
                                key={store.uuid}
                                title={store.name}
                                subtitle={CATEGORY_LABEL[store.category] ?? store.category}
                                onClick={() => handleStoreSelect(store)}
                            />
                        ))}

                        {step === 2 && channels.map((channel) => (
                            <SelectionCard
                                key={channel.uuid}
                                title={channel.name}
                                subtitle={channel.bio}
                                onClick={() => handleChannelSelect(channel)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </DeviceScreenLayout>
    );
};

export default OnboardingPage;
